import { z } from 'zod';
import {
  ATTENDANCE_DAY_STATUSES,
  ATTENDANCE_EVENT_TYPES,
  CLIENT_SOURCES,
  HOLIDAY_SCOPES,
  LEAVE_ACCRUAL_MODES,
  LEAVE_DAY_PARTS,
  LEAVE_LEDGER_REASONS,
  LEAVE_STATUSES,
  WORK_SESSION_EVENT_TYPES,
  WORK_SESSION_STATUSES,
} from '../enums.js';
import {
  dateRangeQuery,
  dayString,
  employeeRef,
  idempotencyKey,
  isoDateTime,
  listQueryWith,
  nonEmpty,
  objectId,
  scopeQuery,
  timestamps,
} from './common.js';
import { announcementAttachment } from './comms.js';

/** Attendance, leave, holidays and desktop work sessions. */

// ── Attendance ──────────────────────────────────────────────────────────────────

export const attendanceEvent = z.object({
  id: objectId,
  type: z.enum(ATTENDANCE_EVENT_TYPES),
  /** SERVER-stamped. The authoritative time. */
  occurredAt: isoDateTime,
  /** What the client claimed. Kept for skew diagnosis, never used in arithmetic. */
  reportedAt: isoDateTime.nullable(),
  clockSkewMs: z.number().int().nullable(),
  source: z.enum(CLIENT_SOURCES),
});

export const attendanceDay = z
  .object({
    id: objectId,
    employee: employeeRef,
    workDate: dayString,
    status: z.enum(ATTENDANCE_DAY_STATUSES),
    firstPunchInAt: isoDateTime.nullable(),
    lastPunchOutAt: isoDateTime.nullable(),
    /** All derived from the event log; never a client-reported total. */
    grossDurationMinutes: z.number().int().nullable(),
    breakDurationMinutes: z.number().int().nullable(),
    netDurationMinutes: z.number().int().nullable(),
    sessionCount: z.number().int(),
    events: z.array(attendanceEvent).optional(),
    requiresCorrection: z.boolean(),
    notes: z.string().nullable(),
  })
  .merge(timestamps);

/**
 * Where a punch was made.
 *
 * OPTIONAL, and it must stay optional. The browser permission can be declined, a
 * desktop may have no position source at all, and indoors a fix can simply never
 * arrive. A punch that failed because the location did not would mean refusing a
 * browser prompt stops someone recording their attendance — so location is metadata
 * attached to a punch, never a condition of making one.
 *
 * Captured at the moment of the punch and at no other time. There is no background
 * position watch anywhere in this product.
 *
 * `accuracyMetres` is stored because a coordinate without it is unreadable: 30m and
 * 3000m look identical once plotted, and only one of them says anything.
 */
export const punchLocation = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  accuracyMetres: z.number().nonnegative().max(100_000).nullable().default(null),
});

/**
 * A punch carries an idempotency key so a retry after a dropped response cannot create
 * a second punch — and `reportedAt` so the server can record clock skew.
 */
export const punchBody = z.object({
  idempotencyKey,
  reportedAt: isoDateTime,
  source: z.enum(CLIENT_SOURCES).default('web'),
  location: punchLocation.nullable().optional(),
});

export const attendanceListQuery = listQueryWith({
  ...dateRangeQuery.shape,
  ...scopeQuery.shape,
  employeeId: objectId.optional(),
  departmentId: objectId.optional(),
  status: z.enum(ATTENDANCE_DAY_STATUSES).optional(),
});

export const attendanceCorrection = z
  .object({
    id: objectId,
    employee: employeeRef,
    workDate: dayString,
    field: z.string(),
    previousValue: z.string().nullable(),
    requestedValue: z.string(),
    reason: z.string(),
    approvalRequestId: objectId.nullable(),
    status: z.enum(['pending', 'approved', 'rejected', 'applied']),
    appliedAt: isoDateTime.nullable(),
  })
  .merge(timestamps);

export const createCorrectionBody = z.object({
  workDate: dayString,
  field: z.enum(['punchIn', 'punchOut', 'break']),
  requestedValue: nonEmpty('Requested value', 40),
  reason: nonEmpty('Reason', 500),
});

export const attendancePolicy = z.object({
  autoFlagMissingPunchOutAfterHours: z.number().int().min(1).max(24),
  allowCorrectionRequests: z.boolean(),
  correctionWindowDays: z.number().int().min(1).max(90),
  breaksAreDeducted: z.boolean(),
  /** A disconnect never auto-punches-out; this only flags for correction (Q-09). */
  autoPunchOutOnDisconnect: z.literal(false),
});

// ── Leave ───────────────────────────────────────────────────────────────────────

export const leaveType = z
  .object({
    id: objectId,
    name: z.string(),
    code: z.string(),
    isBalanced: z.boolean(),
    annualEntitlementDays: z.number().nullable(),
    accrualMode: z.enum(LEAVE_ACCRUAL_MODES).nullable(),
    carryForwardEnabled: z.boolean(),
    carryForwardCapDays: z.number().nullable(),
    prorateOnJoin: z.boolean(),
    requiresProof: z.boolean(),
    allowHalfDay: z.boolean(),
    allowNegativeBalance: z.boolean(),
    countsWeekends: z.boolean(),
    countsHolidays: z.boolean(),
    status: z.enum(['active', 'archived']),
  })
  .merge(timestamps);

export const createLeaveTypeBody = leaveType.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateLeaveTypeBody = createLeaveTypeBody.partial();

export const leaveTypeListQuery = z.object({
  /** HR only — includes archived types in the configure screen. */
  includeArchived: z.coerce.boolean().optional(),
});

/** Per-leave-year allocation, so a request crossing the boundary debits each year once. */
export const leaveYearSplit = z.object({
  leaveYear: z.number().int(),
  days: z.number(),
});

/** Human-readable id shown in lists and detail headers, e.g. LEAVE-0005. */
export const leaveReference = z.string();

export const leaveRequest = z
  .object({
    id: objectId,
    reference: leaveReference,
    employee: employeeRef,
    leaveType: z.object({ id: objectId, name: z.string(), code: z.string() }),
    fromDate: dayString,
    toDate: dayString,
    days: z.number(),
    dayPart: z.enum(LEAVE_DAY_PARTS),
    leaveYearSplit: z.array(leaveYearSplit),
    reason: z.string().nullable(),
    proofFileId: objectId.nullable(),
    attachments: z.array(announcementAttachment),
    approvalRequestId: objectId.nullable(),
    status: z.enum(LEAVE_STATUSES),
    submittedAt: isoDateTime,
    decidedAt: isoDateTime.nullable(),
    cancelledAt: isoDateTime.nullable(),
    /** Who must act next while status is pending, e.g. "Reporting manager" or "HR". */
    currentApprovalStage: z.string().nullable(),
  })
  .merge(timestamps);

export const applyForLeaveBody = z.object({
  leaveTypeId: objectId,
  fromDate: dayString,
  toDate: dayString,
  dayPart: z.enum(LEAVE_DAY_PARTS).default('full'),
  reason: z.string().max(1000).optional(),
  /** @deprecated Prefer attachmentFileIds; kept for older clients. */
  proofFileId: objectId.optional(),
  attachmentFileIds: z.array(objectId).default([]),
});

export const previewLeaveDaysBody = applyForLeaveBody.pick({
  leaveTypeId: true,
  fromDate: true,
  toDate: true,
  dayPart: true,
});

/** The breakdown, not just a number — a disputed day count has to be explainable. */
export const leaveDaysPreview = z.object({
  days: z.number(),
  leaveYearSplit: z.array(leaveYearSplit),
  excludedDates: z.array(
    z.object({ date: dayString, reason: z.enum(['weekend', 'holiday', 'non_working_day']) }),
  ),
  balanceAfter: z.number().nullable(),
  warnings: z.array(z.string()),
});

export const leaveBalance = z.object({
  leaveType: z.object({ id: objectId, name: z.string(), code: z.string() }),
  leaveYear: z.number().int(),
  openingBalance: z.number(),
  accrued: z.number(),
  used: z.number(),
  pending: z.number(),
  carriedForward: z.number(),
  adjustment: z.number(),
  closingBalance: z.number(),
});

/** Every movement, so a balance can be explained line by line. */
export const leaveLedgerEntry = z.object({
  id: objectId,
  leaveType: z.object({ id: objectId, name: z.string() }),
  leaveYear: z.number().int(),
  delta: z.number(),
  reason: z.enum(LEAVE_LEDGER_REASONS),
  leaveRequestId: objectId.nullable(),
  actor: employeeRef.nullable(),
  balanceAfter: z.number(),
  createdAt: isoDateTime,
});

export const adjustBalanceBody = z.object({
  employeeId: objectId,
  leaveTypeId: objectId,
  leaveYear: z.number().int(),
  delta: z.number(),
  reason: nonEmpty('Reason', 500),
});

export const leaveListQuery = listQueryWith({
  ...dateRangeQuery.shape,
  ...scopeQuery.shape,
  status: z.enum(LEAVE_STATUSES).optional(),
  leaveTypeId: objectId.optional(),
  employeeId: objectId.optional(),
});

export const leaveCalendarEntry = z.object({
  employee: employeeRef,
  leaveTypeName: z.string(),
  fromDate: dayString,
  toDate: dayString,
  dayPart: z.enum(LEAVE_DAY_PARTS),
  status: z.enum(LEAVE_STATUSES),
});

export const leaveCalendarQuery = z.object({
  ...dateRangeQuery.shape,
  ...scopeQuery.shape,
});

// ── Holidays ────────────────────────────────────────────────────────────────────

export const holiday = z
  .object({
    id: objectId,
    name: z.string(),
    date: dayString,
    scope: z.enum(HOLIDAY_SCOPES),
    locations: z.array(z.string()),
    status: z.enum(['active', 'inactive']),
  })
  .merge(timestamps);

export const createHolidayBody = z.object({
  name: nonEmpty('Holiday name', 120),
  date: dayString,
  scope: z.enum(HOLIDAY_SCOPES).default('organization'),
  /** A location-scoped holiday affects only employees in those locations. */
  locations: z.array(z.string().max(100)).default([]),
});

export const holidayListQuery = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});

// ── Work sessions (desktop) ─────────────────────────────────────────────────────

export const workSessionDurations = z.object({
  totalMs: z.number().int(),
  activeMs: z.number().int(),
  pausedMs: z.number().int(),
  idleMs: z.number().int(),
  interruptedMs: z.number().int(),
});

export const workSession = z
  .object({
    id: objectId,
    employee: employeeRef,
    status: z.enum(WORK_SESSION_STATUSES),
    startedAt: isoDateTime,
    endedAt: isoDateTime.nullable(),
    lastHeartbeatAt: isoDateTime.nullable(),
    deviceId: z.string().nullable(),
    platform: z.string().nullable(),
    appVersion: z.string().nullable(),
    /** Server-derived from the event log. No client total is ever stored. */
    derivedDurations: workSessionDurations,
    staleTruncatedAt: isoDateTime.nullable(),
  })
  .merge(timestamps);

export const startWorkSessionBody = z.object({
  idempotencyKey,
  reportedAt: isoDateTime,
  deviceId: z.string().max(120),
  platform: z.string().max(60),
  appVersion: z.string().max(40),
});

/**
 * A session transition or heartbeat.
 *
 * `idempotencyKey` is uniquely indexed server-side: a retry after a network drop, or a
 * replay from the desktop offline queue, returns the original result instead of
 * double-counting. `stale_truncate` is server-assigned and rejected from a client.
 */
export const workSessionEventBody = z.object({
  type: z.enum(WORK_SESSION_EVENT_TYPES).refine((t) => t !== 'stale_truncate', {
    message: 'stale_truncate is assigned by the server, not requested by a client',
  }),
  idempotencyKey,
  reportedAt: isoDateTime,
  idleSeconds: z.number().int().min(0).optional(),
  queuedOffline: z.boolean().default(false),
});

export const workSessionEventBatchBody = z.object({
  events: z.array(workSessionEventBody).min(1).max(500),
});

export const workSessionEventResult = z.object({
  idempotencyKey,
  accepted: z.boolean(),
  /** True when this key was already recorded — a safe replay, not an error. */
  duplicate: z.boolean(),
  error: z.string().nullable(),
});

export const heartbeatBody = z.object({
  reportedAt: isoDateTime,
  idleSeconds: z.number().int().min(0),
});

export const workSessionListQuery = listQueryWith({
  ...dateRangeQuery.shape,
  ...scopeQuery.shape,
  employeeId: objectId.optional(),
  status: z.enum(WORK_SESSION_STATUSES).optional(),
});
